import { type VariantProps } from 'class-variance-authority';
import { XIcon } from 'lucide-react';
import { type Variants, motion } from 'motion/react';
import { useMemo } from 'react';

import Icon from '@core/components/Icon';
import { Text } from '@core/components/Text';
import {
  TOAST_ANIMATION,
  TOAST_ICONS,
  TOAST_VARIANTS,
  type ToastVariant,
} from '@core/components/Toast/constants';
import { useToastTimer } from '@core/components/Toast/hooks/useToastTimer';
import { closeButtonVariants, iconVariants, toastVariants } from '@core/components/Toast/styles';
import { useToastStore } from '@core/stores/toastStore';
import { cn } from '@core/utils/styles';

const toastAnimationVariants: Variants = {
  initial: {
    opacity: 0,
    x: '100%',
    scale: 0.95,
    transition: TOAST_ANIMATION.SPRING_CONFIG,
  },
  animate: {
    opacity: 1,
    x: '1%',
    scale: 1,
    transition: TOAST_ANIMATION.SPRING_CONFIG,
  },
  exit: {
    opacity: 0,
    x: '100%',
    scale: 0.95,
    transition: TOAST_ANIMATION.EXIT_CONFIG,
  },
};

export interface ToastProps extends Omit<VariantProps<typeof toastVariants>, 'variant'> {
  id: string;
  title?: string;
  description: string;
  duration?: number;
  variant?: ToastVariant;
}

/**
 * Individual toast component that displays a notification message.
 * Can include a title, description, and variant-specific styling.
 * Supports interaction through hover and close button.
 * @example
 * <ToastComponent title="Success" description="Your action was successful." variant="success" />
 */
export const Toast = ({
  id,
  title,
  description,
  variant = TOAST_VARIANTS.default,
  duration = TOAST_ANIMATION.DURATION,
}: ToastProps) => {
  const removeToast = useToastStore((state) => state.removeToast);
  const { setIsPaused, scope } = useToastTimer(duration, () => removeToast(id));

  const icon = useMemo(() => {
    const iconName = TOAST_ICONS[variant];
    if (!iconName) return null;

    return (
      <div className={iconVariants({ variant })}>
        <Icon name={iconName} className="size-5" />
      </div>
    );
  }, [variant]);

  return (
    <motion.div
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      variants={toastAnimationVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className={toastVariants({ variant })}
    >
      <div className="flex w-full items-center gap-4">
        {icon}

        <div className="flex-1">
          {title && (
            <Text weight="medium" size="base" variant="none">
              {title}
            </Text>
          )}

          {description && (
            <Text size="sm" variant="none" selectable>
              {description}
            </Text>
          )}
        </div>

        <button
          onClick={() => removeToast(id)}
          className={closeButtonVariants({ variant })}
          aria-label="Close toast"
        >
          <XIcon className="h-5 w-5 transition-transform group-hover/close:rotate-180" />
        </button>
      </div>

      {duration > 0 && (
        <motion.div
          ref={scope}
          className={cn('rounded-bl-theme-max absolute right-0 bottom-0 left-0 h-1 origin-left', {
            'bg-primary': variant === TOAST_VARIANTS.default,
            'bg-red-500': variant === TOAST_VARIANTS.destructive,
            'bg-green-500': variant === TOAST_VARIANTS.success,
            'bg-yellow-500': variant === TOAST_VARIANTS.warning,
          })}
        />
      )}
    </motion.div>
  );
};
